import { useEffect, useState } from "react";

import {
  ActionIcon,
  Box,
  BoxProps,
  Checkbox,
  Divider,
  FileButton,
  Flex,
  Group,
  Loader,
  Modal,
  Radio,
  Select as MantineSelect,
  SimpleGrid,
  Table,
  TextInput,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconFileTypePdf,
  IconPlus,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, DatePicker, NumberInput, Select, Text } from "components";
import { AuthorityConstants, ServiceConstants } from "constant";
import { CreditService, ContractService } from "services";
import {
  Bank,
  ContractOptionsResponse,
  ContractPaymentResponse,
  CoverLetterLanguage,
  DocLanguage,
  BankCreditRequestChildResponse,
} from "types";

import { UserUtils } from "utils";

import { ContractAttachment, ContractDetails } from "../hooks/types";
import { useContractAdjustments } from "../hooks/use-contract-adjustments";
import { useContractAttachments } from "../hooks/use-contract-attachments";
import { useContractCore } from "../hooks/use-contract-core";
import { useContractDocuments } from "../hooks/use-contract-documents";
import { useContractPayments } from "../hooks/use-contract-payments";
import { formatDate, formatFileSize, parseContractDate } from "../hooks/utils";

import { ContractPaymentStructure } from "./contract-payment-structure";

const {
  REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID,
  REQUEST_PENDING,
  REQUEST_SUCCESS,
} = ServiceConstants;

const SHIPPING_METHOD_OPTIONS = [
  { value: "EMAIL", labelKey: "bankSelection.unsignedContract.shippingMethod.EMAIL" },
  { value: "POST_OFFICE", labelKey: "bankSelection.unsignedContract.shippingMethod.POST_OFFICE" },
  { value: "OFFICE_MEET", labelKey: "bankSelection.unsignedContract.shippingMethod.OFFICE_MEET" },
];

const OFFICE_MEET_BOOKING_URL = "https://calendar.app.google/ujUAyHPgQCZ1jnaUA";

const getApiErrorMessage = async (error: unknown): Promise<string | undefined> => {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;

  if (!(responseData instanceof Blob)) return undefined;

  try {
    return (JSON.parse(await responseData.text()) as { message?: string }).message;
  } catch {
    return undefined;
  }
};

interface DocumentUploadProps {
  label: string;
  document?: ContractAttachment;
  onUpload: (file: File) => void;
  onDownload?: () => void;
  onDelete?: () => void;
}

const DocumentUpload = ({ label, document, onUpload, onDownload, onDelete }: DocumentUploadProps) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Text size="sm" mb="xs">
        {label}
      </Text>
      {document ? (
        <Flex align="center" gap="md">
          <Flex align="center" gap="sm" style={{ flex: 1 }}>
            <IconFileTypePdf size={36} color="var(--mantine-color-green-6)" />
            <Box>
              <Text size="sm">{document.name}</Text>
              <Text size="xs" c="dimmed">{formatFileSize(document.size)}</Text>
            </Box>
          </Flex>
          <Text size="sm" c="dimmed">{formatDate(document.createdAt)}</Text>
          <Group gap="xs">
            <ActionIcon variant="subtle" size="sm" onClick={onDownload}>
              <IconDownload size={18} color="var(--mantine-color-green-6)" />
            </ActionIcon>
            <ActionIcon variant="subtle" size="sm" onClick={onDelete}>
              <IconX size={18} color="var(--mantine-color-red-6)" />
            </ActionIcon>
          </Group>
        </Flex>
      ) : (
        <FileButton onChange={(file) => file && onUpload(file)} accept="application/pdf">
          {(props) => (
            <Flex
              align="center"
              gap="xs"
              p="sm"
              style={{
                border: "1px dashed var(--mantine-color-gray-4)",
                borderRadius: 8,
                backgroundColor: "white",
                cursor: "pointer",
              }}
              {...props}
            >
              <IconUpload size={20} color="gray" />
              <Text size="sm" c="dimmed">{t("action.selectFile")}</Text>
            </Flex>
          )}
        </FileButton>
      )}
    </Box>
  );
};

interface ContractDetailsSectionProps {
  bank: Bank;
  contractId: number | undefined;
  contractDetails: ContractDetails | undefined;
  contractOptions: ContractOptionsResponse | undefined;
  contractPayments: ContractPaymentResponse[];
  maxCreditAmount?: number;
  scheduledPaymentDate: Date | null;
  banOnRefinancingUntil: Date | null;
  disabled?: boolean;
  sectionKey: string;
  hasContractPaymentAmountMismatch: boolean;
  postponeInstallmentLoading: boolean;
  isContractTypeSelectOpen: boolean;
  isPaymentBankSelectOpen: boolean;
  isStornoSelectOpen: boolean;
  availableBanks: Bank[];
  isAddingContractPayment: boolean;
  newContractPaymentType: string | null;
  newContractPaymentBank: Bank | null;
  newContractPaymentContractNumber: string;
  newContractPaymentAmount: number | undefined;
  isCreatingContractPayment: boolean;
  contractPaymentTypes: string[];
  onContractTypeDropdownOpen: () => void;
  onContractTypeDropdownClose: () => void;
  onPaymentBankDropdownOpen: () => void;
  onPaymentBankDropdownClose: () => void;
  onStornoDropdownOpen: () => void;
  onStornoDropdownClose: () => void;
  updateContractDetails: (updates: Partial<ContractDetails>) => void;
  updateContractField: (name: string, value: unknown) => void;
  onScheduledPaymentDateChange: (value: Date | null) => void;
  onBanOnRefinancingUntilChange: (value: Date | null) => void;
  onOpenPostponeInstallmentModal: () => void;
  onAddContractPayment: () => void;
  onNewContractPaymentTypeChange: (value: string | null) => void;
  onNewContractPaymentBankChange: (value: Bank | null) => void;
  onNewContractPaymentContractNumberChange: (value: string) => void;
  onNewContractPaymentAmountChange: (value: number | undefined) => void;
  onCreateContractPayment: () => void;
  onDeleteContractPayment: (paymentId: number) => void;
  onResetNewContractPaymentForm: () => void;
  isDeletingPayment: (paymentId: number) => boolean;
}

const formatInterestRateValue = (value?: number) => (value != null ? String(value) : "");

const normalizeInterestRateInput = (value: string) => value.replace(",", ".");

const isInterestRateInputValid = (value: string) =>
  /^(\d+([.,]\d{0,2})?|[.,]\d{0,2})?$/.test(value);

const ContractDetailsSection = ({
  bank,
  contractId,
  contractDetails,
  contractOptions,
  contractPayments,
  maxCreditAmount,
  scheduledPaymentDate,
  banOnRefinancingUntil,
  disabled,
  sectionKey,
  hasContractPaymentAmountMismatch,
  postponeInstallmentLoading,
  isContractTypeSelectOpen,
  isPaymentBankSelectOpen,
  isStornoSelectOpen,
  availableBanks,
  isAddingContractPayment,
  newContractPaymentType,
  newContractPaymentBank,
  newContractPaymentContractNumber,
  newContractPaymentAmount,
  isCreatingContractPayment,
  contractPaymentTypes,
  onContractTypeDropdownOpen,
  onContractTypeDropdownClose,
  onPaymentBankDropdownOpen,
  onPaymentBankDropdownClose,
  onStornoDropdownOpen,
  onStornoDropdownClose,
  updateContractDetails,
  updateContractField,
  onScheduledPaymentDateChange,
  onBanOnRefinancingUntilChange,
  onOpenPostponeInstallmentModal,
  onAddContractPayment,
  onNewContractPaymentTypeChange,
  onNewContractPaymentBankChange,
  onNewContractPaymentContractNumberChange,
  onNewContractPaymentAmountChange,
  onCreateContractPayment,
  onDeleteContractPayment,
  onResetNewContractPaymentForm,
  isDeletingPayment,
}: ContractDetailsSectionProps) => {
  const { t } = useTranslation();
  const [interestRateInput, setInterestRateInput] = useState(
    formatInterestRateValue(contractDetails?.interestRate),
  );

  useEffect(() => {
    setInterestRateInput(formatInterestRateValue(contractDetails?.interestRate));
  }, [contractDetails?.interestRate]);

  const handleInterestRateBlur = () => {
    const normalizedValue = normalizeInterestRateInput(interestRateInput.trim());

    if (normalizedValue === "") {
      setInterestRateInput("");
      updateContractDetails({ interestRate: undefined });
      updateContractField("interestRate", null);
      return;
    }

    const parsedValue = Number(normalizedValue);
    if (Number.isNaN(parsedValue)) {
      setInterestRateInput(formatInterestRateValue(contractDetails?.interestRate));
      return;
    }

    setInterestRateInput(formatInterestRateValue(parsedValue));
    updateContractDetails({ interestRate: parsedValue });
    updateContractField("interestRate", parsedValue);
  };

  return (
    <Box style={disabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
      <Text size="md" fw={600} mb="sm">
        {t("bankSelection.aboutCredit.title")}
      </Text>

      <Radio.Group
        label={t("bankSelection.aboutCredit.clientType.label")}
        name={`clientType${sectionKey}`}
        value={contractDetails?.contractClientType || ""}
        onChange={(value) => {
          updateContractDetails({ contractClientType: value as "EXISTING" | "NEW" });
          updateContractField("contractClientType", value);
        }}
      >
        <Flex gap="md" mt="xs">
          {(contractOptions?.contractClientType || ["EXISTING", "NEW"]).map((value) => (
            <Radio
              key={value}
              value={value}
              label={t(`bankSelection.aboutCredit.clientType.${value}`)}
              styles={{ radio: { cursor: "pointer", borderRadius: "50%" }, label: { cursor: "pointer" } }}
            />
          ))}
        </Flex>
      </Radio.Group>

      <SimpleGrid cols={3} mt="md">
        <NumberInput
          label={t("bankSelection.aboutCredit.creditAmount")}
          value={contractDetails?.creditAmount}
          onChange={(value) => {
            const numericValue = typeof value === "number" ? value : undefined;
            updateContractDetails({ creditAmount: numericValue });
            updateContractField("creditAmount", numericValue);
          }}
        />
        <NumberInput
          label={t("bankSelection.aboutCredit.paymentPeriod")}
          value={contractDetails?.paymentPeriod}
          onChange={(value) => {
            const numericValue = typeof value === "number" ? value : undefined;
            updateContractDetails({ paymentPeriod: numericValue });
            updateContractField("paymentPeriod", numericValue);
          }}
          plain
        />
        <MantineSelect
          label={t("bankSelection.aboutCredit.contractType")}
          data={(contractOptions?.contractType || []).map((value) => ({
            value,
            label: t(`bankSelection.aboutCredit.contractTypeOptions.${value}`, { defaultValue: value }),
          }))}
          value={contractDetails?.contractType || null}
          placeholder={t("select.something")}
          onChange={(value) => {
            updateContractDetails({ contractType: value || undefined });
            if (value) updateContractField("contractType", value);
          }}
          onDropdownOpen={onContractTypeDropdownOpen}
          onDropdownClose={onContractTypeDropdownClose}
          rightSection={
            isContractTypeSelectOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />
          }
        />
      </SimpleGrid>

      <SimpleGrid cols={3} mt="md">
        <Box>
          <NumberInput
            label={t("bankSelection.aboutCredit.monthlyInstallment")}
            value={contractDetails?.monthlyInstallment}
            onChange={(value) => {
              const numericValue = typeof value === "number" ? value : undefined;
              updateContractDetails({ monthlyInstallment: numericValue });
              updateContractField("monthlyInstallment", numericValue);
            }}
          />
          {maxCreditAmount != null && (
            <Text size="xs" c="dimmed" mt={4}>
              {`${t("bankSelection.aboutCredit.maxCredit")}: ${maxCreditAmount.toLocaleString("en-US")} CHF`}
            </Text>
          )}
        </Box>
        <Radio.Group
          label={t("bankSelection.aboutCredit.insuredInstallment")}
          name={`insuredInstallment${sectionKey}`}
          value={contractDetails?.creditInsurance ? "YES" : "NO"}
          onChange={(value) => {
            const isInsured = value === "YES";
            updateContractDetails({
              creditInsurance: isInsured,
              theTotalAmountOfTheLoanWithInsurance: isInsured
                ? contractDetails?.theTotalAmountOfTheLoanWithInsurance
                : undefined,
            });
            updateContractField("creditInsurance", isInsured);
            if (!isInsured) {
              updateContractField("theTotalAmountOfTheLoanWithInsurance", null);
            }
          }}
        >
          <Flex gap="md" mt="xs">
            <Radio
              value="YES"
              label={t("yes")}
              styles={{ radio: { cursor: "pointer", borderRadius: "50%" }, label: { cursor: "pointer" } }}
            />
            <Radio
              value="NO"
              label={t("no")}
              styles={{ radio: { cursor: "pointer", borderRadius: "50%" }, label: { cursor: "pointer" } }}
            />
          </Flex>
        </Radio.Group>
        <MantineSelect
          label={t("bankSelection.aboutCredit.paymentBank")}
          data={[{ value: bank, label: bank }]}
          value={bank}
          placeholder="-"
          disabled
          onDropdownOpen={onPaymentBankDropdownOpen}
          onDropdownClose={onPaymentBankDropdownClose}
          rightSection={
            isPaymentBankSelectOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />
          }
        />
      </SimpleGrid>

      <SimpleGrid cols={3} mt="md">
        <TextInput
          label={t("bankSelection.aboutCredit.interestRate")}
          value={interestRateInput}
          onChange={(event) => {
            const nextValue = event.currentTarget.value;
            if (isInterestRateInputValid(nextValue)) {
              setInterestRateInput(nextValue);
            }
          }}
          onBlur={handleInterestRateBlur}
          inputMode="decimal"
          rightSection="%"
          rightSectionWidth={24}
        />
        {contractDetails?.creditInsurance ? (
          <NumberInput
            label={t("bankSelection.aboutCredit.totalAmountWithInsurance")}
            value={contractDetails?.theTotalAmountOfTheLoanWithInsurance}
            onChange={(value) => {
              const numericValue = typeof value === "number" ? value : undefined;
              updateContractDetails({ theTotalAmountOfTheLoanWithInsurance: numericValue });
              updateContractField("theTotalAmountOfTheLoanWithInsurance", numericValue);
            }}
          />
        ) : (
          <Box />
        )}
        <TextInput
          label={t("bankSelection.aboutCredit.contractNumber")}
          value={contractDetails?.contractNumber || ""}
          onChange={(event) => updateContractDetails({ contractNumber: event.currentTarget.value })}
          onBlur={(event) => updateContractField("contractNumber", event.currentTarget.value)}
        />
      </SimpleGrid>

      <SimpleGrid cols={3} mt="md">
        <Box>
          <DatePicker
            label={t("bankSelection.aboutCredit.scheduledPaymentDate")}
            value={scheduledPaymentDate}
            onChange={onScheduledPaymentDateChange}
          />
        </Box>
        <Box>
          <DatePicker
            label={t("bankSelection.aboutCredit.refinancingProhibitedUntil")}
            value={banOnRefinancingUntil}
            onChange={onBanOnRefinancingUntilChange}
          />
        </Box>
        <Box>
          <Text size="sm" mb={4}>
            {`${t("bankSelection.aboutCredit.installmentPostponed")} ${contractDetails?.postponedInstallments ?? 0}/2 ${t("bankSelection.aboutCredit.times")}`}
          </Text>
          <Button
            variant="outline"
            color="gray"
            fullWidth
            onClick={onOpenPostponeInstallmentModal}
            loading={postponeInstallmentLoading}
            disabled={postponeInstallmentLoading || (contractDetails?.postponedInstallments ?? 0) >= 2}
          >
            {t("bankSelection.aboutCredit.postponeInstallment")}
          </Button>
        </Box>
      </SimpleGrid>

      <Box mt="md" style={{ width: "33%" }}>
        <MantineSelect
          label={t("bankSelection.aboutCredit.storno")}
          data={(contractOptions?.cancelType || []).map((value) => ({
            value,
            label: t(`bankSelection.aboutCredit.cancelTypeOptions.${value}`, { defaultValue: value }),
          }))}
          value={contractDetails?.cancelType || null}
          placeholder={t("select.something")}
          onChange={(value) => {
            updateContractDetails({ cancelType: value || undefined });
            if (value) updateContractField("cancelType", value);
          }}
          onDropdownOpen={onStornoDropdownOpen}
          onDropdownClose={onStornoDropdownClose}
          rightSection={isStornoSelectOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
        />
      </Box>

      <ContractPaymentStructure
        contractId={contractId}
        creditAmount={contractDetails?.creditAmount}
        contractPayments={contractPayments}
        contractPaymentTypes={contractPaymentTypes}
        availableBanks={availableBanks}
        hasAmountMismatch={hasContractPaymentAmountMismatch}
        isAddingContractPayment={isAddingContractPayment}
        newContractPaymentType={newContractPaymentType}
        newContractPaymentBank={newContractPaymentBank}
        newContractPaymentContractNumber={newContractPaymentContractNumber}
        newContractPaymentAmount={newContractPaymentAmount}
        isCreatingContractPayment={isCreatingContractPayment}
        onAddContractPayment={onAddContractPayment}
        onNewContractPaymentTypeChange={onNewContractPaymentTypeChange}
        onNewContractPaymentBankChange={onNewContractPaymentBankChange}
        onNewContractPaymentContractNumberChange={onNewContractPaymentContractNumberChange}
        onNewContractPaymentAmountChange={onNewContractPaymentAmountChange}
        onCreateContractPayment={onCreateContractPayment}
        onDeleteContractPayment={onDeleteContractPayment}
        onResetNewContractPaymentForm={onResetNewContractPaymentForm}
        isDeletingPayment={isDeletingPayment}
      />
    </Box>
  );
};

interface PaymentPreparationSectionProps extends BoxProps {
  bank: Bank;
  clientId: number;
  clientFullName: string;
  creditId: number;
  bankCreditRequestId: number;
  bankCreditChildren?: BankCreditRequestChildResponse[];
  availableBanks: Bank[];
  distanceFromOffice?: number;
  onlineAuthEligible?: boolean;
  clientVisaType?: "B" | "C";
  maxCreditAmount?: number;
  maxPaymentPeriod?: number;
  docLanguage?: DocLanguage;
  docLanguageOptions?: DocLanguage[];
  docLanguageOptionsLoading?: boolean;
  docLanguageOptionsError?: boolean;
  onMaxCreditChange: (value: number | undefined) => void;
  onMaxPeriodChange: (value: number | undefined) => void;
  onDocLanguageChange: (language: DocLanguage) => void;
  onRetryDocLanguageOptions: () => void;
}

export const PaymentPreparationSection = ({
  bank,
  clientId: _clientId,
  clientFullName,
  creditId,
  bankCreditRequestId,
  bankCreditChildren = [],
  availableBanks,
  distanceFromOffice,
  onlineAuthEligible,
  clientVisaType,
  maxCreditAmount,
  maxPaymentPeriod,
  docLanguage,
  docLanguageOptions,
  docLanguageOptionsLoading = false,
  docLanguageOptionsError = false,
  onMaxCreditChange,
  onMaxPeriodChange,
  onDocLanguageChange,
  onRetryDocLanguageOptions,
  ...boxProps
}: PaymentPreparationSectionProps) => {
  const { t } = useTranslation();

  const [isDocLanguageSelectOpen, setIsDocLanguageSelectOpen] = useState(false);
  const [isCoverLetterLanguageSelectOpen, setIsCoverLetterLanguageSelectOpen] = useState(false);
  const [isShippingSelectOpen, setIsShippingSelectOpen] = useState(false);
  const [isContractTypeSelectOpen, setIsContractTypeSelectOpen] = useState(false);
  const [isPaymentBankSelectOpen, setIsPaymentBankSelectOpen] = useState(false);
  const [isStornoSelectOpen, setIsStornoSelectOpen] = useState(false);
  const [postponeInstallmentModalOpen, setPostponeInstallmentModalOpen] = useState(false);
  const [isCoverLetterGenerating, setIsCoverLetterGenerating] = useState(false);
  const [isBetreuungskostenformularGenerating, setIsBetreuungskostenformularGenerating] = useState(false);

  const {
    contractId,
    contractDetails,
    previousContractDetails,
    contractOptions,
    shippingMethod,
    scheduledPaymentDate,
    banOnRefinancingUntil,
    updateContractDetails,
    updateContractField,
    handleShippingMethodChange,
    handleScheduledPaymentDateChange,
    handleBanOnRefinancingUntilChange,
  } = useContractCore(bankCreditRequestId, bank);

  const {
    zinzDocument,
    unsignedContracts,
    signedContract,
    otherDocuments,
    handleZinzUpload,
    handleZinzDelete,
    handleZinzDownload,
    handleUnsignedContractUpload,
    handleUnsignedContractDownload,
    handleUnsignedContractDelete,
    handleSignedContractUpload,
    handleSignedContractDownload,
    handleSignedContractDelete,
    handleOtherDocumentUpload,
    handleOtherDocumentDownload,
    handleOtherDocumentDelete,
  } = useContractAttachments(bankCreditRequestId, bank, contractId);

  const {
    fixedDocuments,
    dynamicDocuments,
    isAddingContractDocument,
    newContractDocumentType,
    availableDocumentTypeOptions,
    isCreatingContractDocument,
    isDeletingContractDocument,
    setIsAddingContractDocument,
    setNewContractDocumentType,
    isDocumentChecked,
    getDocumentTypeLabel,
    handleContractDocumentChange,
    handleCreateContractDocument,
    handleDeleteContractDocument,
  } = useContractDocuments(bank, contractId, clientVisaType);

  const {
    contractPayments,
    contractPaymentTypes,
    isAddingContractPayment,
    newContractPaymentType,
    newContractPaymentBank,
    newContractPaymentContractNumber,
    newContractPaymentAmount,
    isCreatingContractPayment,
    contractPaymentsTotalAmount,
    setIsAddingContractPayment,
    setNewContractPaymentType,
    setNewContractPaymentBank,
    setNewContractPaymentContractNumber,
    setNewContractPaymentAmount,
    handleCreateContractPayment,
    handleDeleteContractPayment,
    resetNewContractPaymentForm,
    isDeletingPayment,
  } = useContractPayments(contractId);

  const {
    contractPayments: previousContractPayments,
    contractPaymentsTotalAmount: previousContractPaymentsTotalAmount,
  } = useContractPayments(previousContractDetails?.id);

  const {
    contractAdjustments,
    anpassungOpen,
    anpassungCreditAmount,
    anpassungPaymentPeriod,
    anpassungMonthlyInstallment,
    anpassungCreditInsurance,
    anpassungComment,
    isCreatingContractAdjustment,
    setAnpassungOpen,
    setAnpassungCreditAmount,
    setAnpassungPaymentPeriod,
    setAnpassungMonthlyInstallment,
    setAnpassungCreditInsurance,
    setAnpassungComment,
    openAnpassungModal,
    handleAnpassungSubmit,
    handleAdjustmentStatusChange,
    isAdjustmentActionPending,
  } = useContractAdjustments(bankCreditRequestId, bank, contractId, contractDetails);

  const postponeInstallmentResponse = useSelector(
    (state: any) => state.service.object[REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID]
  );

  useEffect(() => {
    if (postponeInstallmentResponse?.status === REQUEST_SUCCESS) {
      updateContractDetails({ postponedInstallments: postponeInstallmentResponse.data });
      setPostponeInstallmentModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postponeInstallmentResponse]);

  const canViewUnsignedContract =
    UserUtils.hasAuthority(AuthorityConstants.ROLE_CON) &&
    UserUtils.hasAuthority(AuthorityConstants.ROLE_CON_ATT);
  const canViewAtt = UserUtils.hasAuthority(AuthorityConstants.ROLE_CON_ATT);
  const canViewAttOther = UserUtils.hasAuthority(AuthorityConstants.ROLE_CON_ATT_OTHER);
  const canViewAdj = UserUtils.hasAuthority(AuthorityConstants.ROLE_CON_ADJ);
  const mappedDocLanguageOptions = (docLanguageOptions ?? []).map((language) => ({
    value: language,
    label: language,
  }));
  const mappedCoverLetterLanguageOptions = (contractOptions?.coverLetterLanguage ?? []).map((language) => ({
    value: language,
    label: language,
  }));

  const hasUnsignedContract = unsignedContracts.length > 0;
  const hasPreviousContract = !!previousContractDetails?.id;
  const topContractDetails = previousContractDetails ?? contractDetails;
  const topScheduledPaymentDate = parseContractDate(topContractDetails?.scheduledPaymentDate);
  const topBanOnRefinancingUntil = parseContractDate(topContractDetails?.banOnRefinancingUntil);
  const topContractPayments = hasPreviousContract ? previousContractPayments : contractPayments;
  const topContractPaymentsTotalAmount = hasPreviousContract
    ? previousContractPaymentsTotalAmount
    : contractPaymentsTotalAmount;

  const hasTopContractPaymentAmountMismatch =
    topContractPaymentsTotalAmount > 0 &&
    (topContractDetails?.creditAmount ?? 0) > 0 &&
    topContractPaymentsTotalAmount !== topContractDetails?.creditAmount;

  const hasCurrentContractPaymentAmountMismatch =
    contractPaymentsTotalAmount > 0 &&
    (contractDetails?.creditAmount ?? 0) > 0 &&
    contractPaymentsTotalAmount !== contractDetails?.creditAmount;

  const handlePostponeInstallment = async () => {
    if (!creditId || (contractDetails?.postponedInstallments ?? 0) >= 2) return;
    await CreditService.creditPostponeInstalment(creditId);
  };

  const handleCoverLetterGenerate = async () => {
    if (!contractId || isCoverLetterGenerating) return;
    setIsCoverLetterGenerating(true);
    try {
      await ContractService.downloadCoverLetter(
        contractId,
        contractDetails?.coverLetterLanguage || "DE",
        clientFullName,
      );
    } catch (error) {
      console.error("Cover letter download failed:", error);
      notifications.show({
        title: t("bankSelection.otherDocumentation.generateCoverLetter"),
        message: t("bankSelection.details.generateXmlError"),
        color: "red",
      });
    } finally {
      setIsCoverLetterGenerating(false);
    }
  };

  const handleBetreuungskostenformularGenerate = async () => {
    if (!contractId || isBetreuungskostenformularGenerating) return;

    const includedChildren = bankCreditChildren.filter((child) => child.childIncluded !== false);
    const hasInvalidIncludedChildren = includedChildren.some((child) => {
      const firstName = child.firstName?.trim();
      const lastName = child.lastName?.trim();

      return !firstName || !lastName || !child.birthdate;
    });

    if (hasInvalidIncludedChildren) {
      notifications.show({
        title: t("bankSelection.otherDocumentation.generateBetreuungskostenformular"),
        message: t("bankSelection.otherDocumentation.generateBetreuungskostenformularValidationError"),
        color: "red",
      });
      return;
    }

    setIsBetreuungskostenformularGenerating(true);
    try {
      await ContractService.downloadBetreuungskostenformular(
        contractId,
        docLanguage || "DE",
        clientFullName,
      );
    } catch (error) {
      console.error("Betreuungskostenformular download failed:", error);
      notifications.show({
        title: t("bankSelection.otherDocumentation.generateBetreuungskostenformular"),
        message: (await getApiErrorMessage(error)) ?? t("bankSelection.details.generateXmlError"),
        color: "red",
      });
    } finally {
      setIsBetreuungskostenformularGenerating(false);
    }
  };

  const renderAdjustmentStatus = (adjustment: { id: number; status: string }) => {
    const isPending = isAdjustmentActionPending(adjustment.id);

    if (adjustment.status !== "PENDING") {
      return (
        <Text size="sm">
          {t(`bankSelection.anpassung.table.statusOptions.${adjustment.status}`, {
            defaultValue: adjustment.status,
          })}
        </Text>
      );
    }

    return (
      <Select
        value={null}
        placeholder={t("bankSelection.anpassung.table.statusPlaceholder")}
        data={[
          { value: "APPROVED", label: t("bankSelection.anpassung.table.statusOptions.APPROVED") },
          { value: "REJECTED", label: t("bankSelection.anpassung.table.statusOptions.REJECTED") },
        ]}
        onChange={(value) => handleAdjustmentStatusChange(adjustment.id, value)}
        disabled={isPending}
        allowDeselect={false}
        checkIconPosition="right"
      />
    );
  };

  return (
    <Box
      p="md"
      style={{
        backgroundColor: "rgba(241, 242, 244, 1)",
        borderRadius: 12,
        border: "1px solid rgba(50, 51, 67, 0.15)",
      }}
      {...boxProps}
    >
      {/* Header row */}
      <Flex justify="space-between" align="center" gap="md" mb="md" wrap="wrap">
        <Flex gap="xl" wrap="wrap">
          <Text size="sm" c="dimmed">
            {`${t("bankSelection.paymentPreparation.distanceFromOffice")}: ${distanceFromOffice ?? "-"}km`}
          </Text>
          {bank === "BANK_NOW" && (
            <Flex align="center" gap="xs">
              <Text size="sm" c="dimmed">
                {t("bankSelection.paymentPreparation.onlineAuthEligible")}
              </Text>
              {onlineAuthEligible ? (
                <IconCheck size={18} color="var(--mantine-color-green-6)" />
              ) : (
                <IconX size={18} color="var(--mantine-color-red-6)" />
              )}
            </Flex>
          )}
        </Flex>

      </Flex>

      <Text fw={600} c="var(--mantine-color-primary-6)" size="lg" mb="md">
        {t("bankSelection.paymentPreparation.title")}
      </Text>

      {/* Top fields */}
      <SimpleGrid cols={4}>
        <NumberInput
          label={t("bankSelection.paymentPreparation.maxCredit")}
          value={maxCreditAmount}
          onChange={(value) => onMaxCreditChange(typeof value === "number" ? value : undefined)}
          min={0}
          thousandSeparator="'"
          suffix=" CHF"
        />
        <NumberInput
          label={t("bankSelection.paymentPreparation.maxPeriod")}
          value={maxPaymentPeriod}
          onChange={(value) => onMaxPeriodChange(typeof value === "number" ? value : undefined)}
          min={0}
          plain={true}
          labelProps={{ size: "sm", mb: 2 }}
          mb={6}
        />
        {bank === "BANK_NOW" && (
          <TextInput
            label={t("bankSelection.paymentPreparation.zinzMatrix")}
            value={zinzDocument?.name || ""}
            placeholder={t("action.selectFile")}
            readOnly
            labelProps={{ size: "sm", mb: 2 }}
            rightSection={
              zinzDocument ? (
                <Group gap={4}>
                  <ActionIcon variant="subtle" size="sm" onClick={handleZinzDownload}>
                    <IconDownload size={16} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" color="red" size="sm" onClick={handleZinzDelete}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ) : (
                <FileButton onChange={(file) => file && handleZinzUpload(file)} accept="application/pdf">
                  {(props) => (
                    <ActionIcon variant="subtle" size="sm" {...props}>
                      <IconUpload size={16} />
                    </ActionIcon>
                  )}
                </FileButton>
              )
            }
            rightSectionWidth={zinzDocument ? 60 : 30}
          />
        )}
        <Select
          label={t("bankSelection.paymentPreparation.docLanguage")}
          value={docLanguage || null}
          onChange={(value) => value && onDocLanguageChange(value as DocLanguage)}
          data={mappedDocLanguageOptions}
          placeholder="-"
          disabled={docLanguageOptionsLoading || docLanguageOptionsError}
          allowDeselect={false}
          withCheckIcon={false}
          rightSection={
            docLanguageOptionsLoading ? (
              <Loader size="xs" />
            ) : isDocLanguageSelectOpen ? (
              <IconChevronUp size={14} />
            ) : (
              <IconChevronDown size={14} />
            )
          }
          onDropdownOpen={() => setIsDocLanguageSelectOpen(true)}
          onDropdownClose={() => setIsDocLanguageSelectOpen(false)}
        />
        {docLanguageOptionsError && (
          <Flex align="center" gap="xs">
            <Text size="xs" c="red">
              {t("bankSelection.paymentPreparation.docLanguageLoadError")}
            </Text>
            <Button variant="subtle" size="compact-xs" onClick={onRetryDocLanguageOptions}>
              {t("bankSelection.retry")}
            </Button>
          </Flex>
        )}
      </SimpleGrid>

      {canViewUnsignedContract && (
        <>
          <Divider my="md" />

          {/* Unsigned contract */}
          <Flex align="flex-end" justify="space-between" gap="sm" wrap="wrap" style={{ width: "100%" }}>
            <Flex align="center" gap="xs">
              <Text size="md" fw={600}>
                {t("bankSelection.unsignedContract.title")}
              </Text>
              <MantineSelect
                value={shippingMethod}
                onChange={handleShippingMethodChange}
                data={SHIPPING_METHOD_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: t(opt.labelKey),
                }))}
                placeholder={t("select.something")}
                size="xs"
                allowDeselect={false}
                withCheckIcon={false}
                rightSection={
                  isShippingSelectOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                }
                onDropdownOpen={() => setIsShippingSelectOpen(true)}
                onDropdownClose={() => setIsShippingSelectOpen(false)}
                styles={{
                  input: { width: 130, minHeight: 28, borderRadius: 6 },
                }}
              />
            </Flex>
            {shippingMethod === "OFFICE_MEET" && (
              <Button
                size="xs"
                variant="outline"
                color="gray"
                onClick={() => window.open(OFFICE_MEET_BOOKING_URL, "_blank", "noopener,noreferrer")}
              >
                {t("bankSelection.unsignedContract.bookAppointment")}
              </Button>
            )}
          </Flex>

          {shippingMethod && (
            <SimpleGrid cols={2} mt="md">
              <DocumentUpload
                label={t("bankSelection.unsignedContract.part1")}
                document={unsignedContracts[0]}
                onUpload={handleUnsignedContractUpload}
                onDownload={() => unsignedContracts[0] && handleUnsignedContractDownload(unsignedContracts[0].id)}
                onDelete={() => unsignedContracts[0] && handleUnsignedContractDelete(unsignedContracts[0].id)}
              />
              {shippingMethod === "EMAIL" && (
                <DocumentUpload
                  label={t("bankSelection.unsignedContract.part2")}
                  document={unsignedContracts[1]}
                  onUpload={handleUnsignedContractUpload}
                  onDownload={() => unsignedContracts[1] && handleUnsignedContractDownload(unsignedContracts[1].id)}
                  onDelete={() => unsignedContracts[1] && handleUnsignedContractDelete(unsignedContracts[1].id)}
                />
              )}
            </SimpleGrid>
          )}
        </>
      )}

      {hasUnsignedContract && (
        <>
          {canViewAttOther && (
            <>
              <Divider my="md" />

              {/* Other documentation */}
              <Flex align="center" gap="md" mb="md">
                <Text size="md" fw={600}>
                  {t("bankSelection.otherDocumentation.title")}
                </Text>
              </Flex>

              <SimpleGrid cols={2} spacing="md" mb="md">
                <Flex direction="column" gap="sm">
                  {fixedDocuments.map(({ type }) => (
                    <Flex align="center" gap="sm" key={type}>
                      <Checkbox
                        checked={isDocumentChecked(type)}
                        onChange={(event) =>
                          handleContractDocumentChange(type, event.currentTarget.checked)
                        }
                        styles={{ input: { cursor: "pointer" } }}
                      />
                      <TextInput
                        value={getDocumentTypeLabel(type)}
                        readOnly
                        style={{ flex: 1 }}
                        styles={{ input: { backgroundColor: "white" } }}
                      />
                    </Flex>
                  ))}
                  {dynamicDocuments.map((document) => (
                    <Flex align="center" gap="sm" key={document.id}>
                      <Checkbox
                        checked={isDocumentChecked(document.type)}
                        onChange={(event) =>
                          handleContractDocumentChange(document.type, event.currentTarget.checked)
                        }
                        styles={{ input: { cursor: "pointer" } }}
                      />
                      <TextInput
                        value={getDocumentTypeLabel(document.type)}
                        readOnly
                        style={{ flex: 1 }}
                        styles={{ input: { backgroundColor: "white" } }}
                        rightSection={
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => handleDeleteContractDocument(document.id)}
                            disabled={isDeletingContractDocument}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        }
                        rightSectionWidth={36}
                      />
                    </Flex>
                  ))}
                  {isAddingContractDocument && (
                    <Flex align="center" gap="sm">
                      <MantineSelect
                        value={newContractDocumentType}
                        onChange={setNewContractDocumentType}
                        data={availableDocumentTypeOptions.map((option) => ({
                          value: option.value,
                          label: option.label,
                        }))}
                        placeholder={t("bankSelection.otherDocumentation.selectPlaceholder")}
                        style={{ flex: 1 }}
                        allowDeselect={false}
                        searchable
                        disabled={isCreatingContractDocument}
                      />
                      <ActionIcon
                        variant="subtle"
                        color="green"
                        onClick={handleCreateContractDocument}
                        disabled={!newContractDocumentType || isCreatingContractDocument}
                      >
                        <IconCheck size={18} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          setIsAddingContractDocument(false);
                          setNewContractDocumentType(null);
                        }}
                        disabled={isCreatingContractDocument}
                      >
                        <IconX size={18} />
                      </ActionIcon>
                    </Flex>
                  )}
                  {!isAddingContractDocument && (
                    <Button
                      size="compact-xs"
                      variant="transparent"
                      c="var(--mantine-color-gray-6)"
                      justify="flex-start"
                      px={0}
                      onClick={() => setIsAddingContractDocument(true)}
                      disabled={!contractId || availableDocumentTypeOptions.length === 0}
                    >
                      <>
                        <Box mr={4}><IconPlus size={16} /></Box>
                        {t("bankSelection.otherDocumentation.add")}
                      </>
                    </Button>
                  )}
                </Flex>

                <Flex direction="column" gap="sm">
                  {otherDocuments.map((document) => (
                    <Flex
                      key={document.id}
                      align="center"
                      gap="sm"
                      px={12}
                      py={4}
                      h={36}
                      style={{
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: 4,
                        backgroundColor: "white",
                        boxSizing: "border-box",
                      }}
                    >
                      <Flex align="center" gap={8} style={{ flex: 1, minWidth: 0 }}>
                        <IconFileTypePdf size={16} color="var(--mantine-color-green-6)" />
                        <Box style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
                          <Flex align="center" gap={6}>
                            <Text
                              size="xs"
                              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              {document.name}
                            </Text>
                            <Text size="xs" c="dimmed">{formatFileSize(document.size)}</Text>
                          </Flex>
                        </Box>
                      </Flex>
                      <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                        {formatDate(document.createdAt)}
                      </Text>
                      <Group gap={4}>
                        <ActionIcon variant="subtle" size="sm" onClick={() => handleOtherDocumentDownload(document)}>
                          <IconDownload size={18} color="var(--mantine-color-green-6)" />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => handleOtherDocumentDelete(document.id)}
                        >
                          <IconX size={18} color="var(--mantine-color-red-6)" />
                        </ActionIcon>
                      </Group>
                    </Flex>
                  ))}
                  <FileButton onChange={(file) => file && handleOtherDocumentUpload(file)} accept="application/pdf">
                    {(props) => (
                      <Flex
                        align="center"
                        gap="xs"
                        px={12}
                        py={4}
                        h={36}
                        style={{
                          border: "1px dashed var(--mantine-color-gray-4)",
                          borderRadius: 4,
                          backgroundColor: "white",
                          cursor: "pointer",
                          boxSizing: "border-box",
                        }}
                        {...props}
                      >
                        <IconUpload size={20} color="gray" />
                        <Text size="sm" c="dimmed">{t("action.selectFile")}</Text>
                      </Flex>
                    )}
                  </FileButton>
                </Flex>
              </SimpleGrid>

              {bank === "BANK_NOW" && (
                <>
                  <Divider my="md" />

                  {/* Childcare costs */}
                  <Box style={{ width: "100%", maxWidth: 240 }} mb="md">
                    <NumberInput
                      label={t("bankSelection.otherDocumentation.childcareCosts")}
                      value={contractDetails?.childcareCosts}
                      onChange={(value) => {
                        const numericValue = typeof value === "number" ? value : undefined;
                        updateContractDetails({ childcareCosts: numericValue });
                        updateContractField("childcareCosts", numericValue);
                      }}
                    />
                  </Box>

                  <Divider my="md" />
                </>
              )}

              {/* Document generation buttons */}
              <Flex align="flex-end" gap="md" wrap="wrap">
                <Box style={{ width: "100%", maxWidth: 240 }}>
                  <Select
                    label={t("bankSelection.paymentPreparation.coverLetterLanguage")}
                    value={contractDetails?.coverLetterLanguage || "DE"}
                    onChange={(value) => {
                      if (!value) return;
                      const language = value as CoverLetterLanguage;
                      updateContractDetails({ coverLetterLanguage: language });
                      updateContractField("coverLetterLanguage", language);
                    }}
                    data={mappedCoverLetterLanguageOptions}
                    placeholder="-"
                    allowDeselect={false}
                    withCheckIcon={false}
                    rightSection={
                      isCoverLetterLanguageSelectOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                    }
                    onDropdownOpen={() => setIsCoverLetterLanguageSelectOpen(true)}
                    onDropdownClose={() => setIsCoverLetterLanguageSelectOpen(false)}
                  />
                </Box>
                <Box style={{ flex: 1, minWidth: 260 }}>
                  <Button
                    variant="filled"
                    color="gray"
                    fullWidth
                    loading={isCoverLetterGenerating}
                    onClick={handleCoverLetterGenerate}
                  >
                    {t("bankSelection.otherDocumentation.generateCoverLetter")}
                  </Button>
                </Box>
                <Box style={{ flex: 1, minWidth: 260 }}>
                  <Button
                    variant="filled"
                    color="gray"
                    fullWidth
                    loading={isBetreuungskostenformularGenerating}
                    onClick={handleBetreuungskostenformularGenerate}
                  >
                    {t("bankSelection.otherDocumentation.generateBetreuungskostenformular")}
                  </Button>
                </Box>
              </Flex>
            </>
          )}

          {canViewAtt && (
            <>
              <Divider my="md" />

              {/* Signed contract */}
              <Text size="md" fw={600} mb="sm">
                {t("bankSelection.signedContract.title")}
              </Text>

              <Box style={{ width: "50%" }}>
                <DocumentUpload
                  label={t("bankSelection.signedContract.title")}
                  document={signedContract}
                  onUpload={handleSignedContractUpload}
                  onDownload={handleSignedContractDownload}
                  onDelete={handleSignedContractDelete}
                />
              </Box>
            </>
          )}

          <Divider my="md" />

          <ContractDetailsSection
            bank={bank}
            contractId={contractId}
            contractDetails={topContractDetails}
            contractOptions={contractOptions}
            contractPayments={topContractPayments}
            maxCreditAmount={maxCreditAmount}
            scheduledPaymentDate={topScheduledPaymentDate}
            banOnRefinancingUntil={topBanOnRefinancingUntil}
            disabled={hasPreviousContract}
            sectionKey=""
            hasContractPaymentAmountMismatch={hasTopContractPaymentAmountMismatch}
            postponeInstallmentLoading={postponeInstallmentResponse?.status === REQUEST_PENDING}
            isContractTypeSelectOpen={isContractTypeSelectOpen}
            isPaymentBankSelectOpen={isPaymentBankSelectOpen}
            isStornoSelectOpen={isStornoSelectOpen}
            availableBanks={availableBanks}
            isAddingContractPayment={isAddingContractPayment}
            newContractPaymentType={newContractPaymentType}
            newContractPaymentBank={newContractPaymentBank}
            newContractPaymentContractNumber={newContractPaymentContractNumber}
            newContractPaymentAmount={newContractPaymentAmount}
            isCreatingContractPayment={isCreatingContractPayment}
            contractPaymentTypes={contractPaymentTypes}
            onContractTypeDropdownOpen={() => setIsContractTypeSelectOpen(true)}
            onContractTypeDropdownClose={() => setIsContractTypeSelectOpen(false)}
            onPaymentBankDropdownOpen={() => setIsPaymentBankSelectOpen(true)}
            onPaymentBankDropdownClose={() => setIsPaymentBankSelectOpen(false)}
            onStornoDropdownOpen={() => setIsStornoSelectOpen(true)}
            onStornoDropdownClose={() => setIsStornoSelectOpen(false)}
            updateContractDetails={updateContractDetails}
            updateContractField={updateContractField}
            onScheduledPaymentDateChange={handleScheduledPaymentDateChange}
            onBanOnRefinancingUntilChange={handleBanOnRefinancingUntilChange}
            onOpenPostponeInstallmentModal={() => setPostponeInstallmentModalOpen(true)}
            onAddContractPayment={() => setIsAddingContractPayment(true)}
            onNewContractPaymentTypeChange={setNewContractPaymentType}
            onNewContractPaymentBankChange={setNewContractPaymentBank}
            onNewContractPaymentContractNumberChange={setNewContractPaymentContractNumber}
            onNewContractPaymentAmountChange={setNewContractPaymentAmount}
            onCreateContractPayment={handleCreateContractPayment}
            onDeleteContractPayment={handleDeleteContractPayment}
            onResetNewContractPaymentForm={resetNewContractPaymentForm}
            isDeletingPayment={isDeletingPayment}
          />

          {/* Postpone installment modal */}
          <Modal
            opened={postponeInstallmentModalOpen}
            onClose={() => setPostponeInstallmentModalOpen(false)}
            withCloseButton={false}
            centered
          >
            <Text ta="center" mt={24} mb={12} fw={700}>
              {t("credit.postponeInstallment.modal.title")}
            </Text>
            <Text ta="center" mb={24}>
              {t("credit.postponeInstallment.modal.description")}
            </Text>
            <Flex mt={32} mb={12} justify="space-between" align="center" px={32}>
              <Button
                variant="subtle"
                color="gray"
                onClick={() => setPostponeInstallmentModalOpen(false)}
                disabled={postponeInstallmentResponse?.status === REQUEST_PENDING}
              >
                {t("credit.postponeInstallment.modal.cancel")}
              </Button>
              <Button
                onClick={handlePostponeInstallment}
                loading={postponeInstallmentResponse?.status === REQUEST_PENDING}
                disabled={postponeInstallmentResponse?.status === REQUEST_PENDING}
              >
                {t("credit.postponeInstallment.modal.submit")}
              </Button>
            </Flex>
          </Modal>

          {canViewAdj && (
            <>
              {/* Anpassung button */}
              <Button
                variant="filled"
                color="gray"
                fullWidth
                mt="xl"
                onClick={openAnpassungModal}
              >
                {t("bankSelection.anpassung.button")}
              </Button>

              {/* Anpassung modal */}
              <Modal
                opened={anpassungOpen}
                onClose={() => setAnpassungOpen(false)}
                centered
                withCloseButton={false}
                size={520}
                radius={12}
                padding="xl"
              >
                <Text ta="center" fw={600} size="lg" mb="xl">
                  {t("bankSelection.anpassung.form.title")}
                </Text>

                <SimpleGrid cols={2} spacing="md">
                  <NumberInput
                    label={t("bankSelection.anpassung.form.creditAmount")}
                    value={anpassungCreditAmount}
                    onChange={(value) =>
                      setAnpassungCreditAmount(typeof value === "number" ? value : undefined)
                    }
                  />
                  <NumberInput
                    label={t("bankSelection.anpassung.form.paymentPeriod")}
                    value={anpassungPaymentPeriod}
                    onChange={(value) =>
                      setAnpassungPaymentPeriod(typeof value === "number" ? value : undefined)
                    }
                    plain
                  />
                  <NumberInput
                    label={t("bankSelection.anpassung.form.installmentAmount")}
                    value={anpassungMonthlyInstallment}
                    onChange={(value) =>
                      setAnpassungMonthlyInstallment(typeof value === "number" ? value : undefined)
                    }
                  />
                  <Radio.Group
                    label={t("bankSelection.anpassung.form.amountWithInsurance")}
                    name="anpassungCreditInsurance"
                    value={anpassungCreditInsurance}
                    onChange={setAnpassungCreditInsurance}
                  >
                    <Flex gap="md" mt="xs">
                      <Radio
                        value="YES"
                        label={t("yes")}
                        styles={{ radio: { cursor: "pointer", borderRadius: "50%" }, label: { cursor: "pointer" } }}
                      />
                      <Radio
                        value="NO"
                        label={t("no")}
                        styles={{ radio: { cursor: "pointer", borderRadius: "50%" }, label: { cursor: "pointer" } }}
                      />
                    </Flex>
                  </Radio.Group>
                </SimpleGrid>

                <Box mt="lg">
                  <Textarea
                    label={t("bankSelection.anpassung.form.comment")}
                    placeholder={t("bankSelection.anpassung.form.commentPlaceholder")}
                    value={anpassungComment}
                    onChange={(event) => setAnpassungComment(event.currentTarget.value)}
                    minRows={2}
                    autosize
                  />
                </Box>

                <Group justify="center" mt="xl">
                  <Button variant="outline" color="gray" onClick={() => setAnpassungOpen(false)}>
                    {t("bankSelection.anpassung.form.cancel")}
                  </Button>
                  <Button
                    variant="filled"
                    color="green"
                    onClick={() => handleAnpassungSubmit(isCreatingContractAdjustment)}
                    loading={isCreatingContractAdjustment}
                  >
                    {t("bankSelection.anpassung.form.submit")}
                  </Button>
                </Group>
              </Modal>

              {/* Anpassung history */}
              <Table mt="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("bankSelection.anpassung.table.dateSent")}</Table.Th>
                    <Table.Th>{t("bankSelection.anpassung.table.creditAmount")}</Table.Th>
                    <Table.Th>{t("bankSelection.anpassung.table.paymentPeriod")}</Table.Th>
                    <Table.Th>{t("bankSelection.anpassung.table.installmentAmount")}</Table.Th>
                    <Table.Th>{t("bankSelection.anpassung.table.amountWithInsurance")}</Table.Th>
                    <Table.Th>{t("bankSelection.anpassung.table.status")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {contractAdjustments.length > 0 ? (
                    contractAdjustments.map((adjustment) => (
                      <Table.Tr key={adjustment.id}>
                        <Table.Td>{formatDate(adjustment.info?.createdAt)}</Table.Td>
                        <Table.Td>
                          {adjustment.creditAmount != null
                            ? `${adjustment.creditAmount.toLocaleString("de-CH")} CHF`
                            : "-"}
                        </Table.Td>
                        <Table.Td>{adjustment.paymentPeriod != null ? adjustment.paymentPeriod : "-"}</Table.Td>
                        <Table.Td>
                          {adjustment.monthlyInstallment != null
                            ? `${adjustment.monthlyInstallment.toLocaleString("de-CH")} CHF`
                            : "-"}
                        </Table.Td>
                        <Table.Td>{adjustment.creditInsurance ? t("yes") : t("no")}</Table.Td>
                        <Table.Td>{renderAdjustmentStatus(adjustment)}</Table.Td>
                      </Table.Tr>
                    ))
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Text size="sm" c="dimmed">
                          {t("bankSelection.anpassung.table.empty")}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </>
          )}

          {hasPreviousContract && (
            <>
              <Divider my="md" />

              <ContractDetailsSection
                bank={bank}
                contractId={contractId}
                contractDetails={contractDetails}
                contractOptions={contractOptions}
                contractPayments={contractPayments}
                maxCreditAmount={maxCreditAmount}
                scheduledPaymentDate={scheduledPaymentDate}
                banOnRefinancingUntil={banOnRefinancingUntil}
                sectionKey="Approved"
                hasContractPaymentAmountMismatch={hasCurrentContractPaymentAmountMismatch}
                postponeInstallmentLoading={postponeInstallmentResponse?.status === REQUEST_PENDING}
                isContractTypeSelectOpen={isContractTypeSelectOpen}
                isPaymentBankSelectOpen={isPaymentBankSelectOpen}
                isStornoSelectOpen={isStornoSelectOpen}
                availableBanks={availableBanks}
                isAddingContractPayment={isAddingContractPayment}
                newContractPaymentType={newContractPaymentType}
                newContractPaymentBank={newContractPaymentBank}
                newContractPaymentContractNumber={newContractPaymentContractNumber}
                newContractPaymentAmount={newContractPaymentAmount}
                isCreatingContractPayment={isCreatingContractPayment}
                contractPaymentTypes={contractPaymentTypes}
                onContractTypeDropdownOpen={() => setIsContractTypeSelectOpen(true)}
                onContractTypeDropdownClose={() => setIsContractTypeSelectOpen(false)}
                onPaymentBankDropdownOpen={() => setIsPaymentBankSelectOpen(true)}
                onPaymentBankDropdownClose={() => setIsPaymentBankSelectOpen(false)}
                onStornoDropdownOpen={() => setIsStornoSelectOpen(true)}
                onStornoDropdownClose={() => setIsStornoSelectOpen(false)}
                updateContractDetails={updateContractDetails}
                updateContractField={updateContractField}
                onScheduledPaymentDateChange={handleScheduledPaymentDateChange}
                onBanOnRefinancingUntilChange={handleBanOnRefinancingUntilChange}
                onOpenPostponeInstallmentModal={() => setPostponeInstallmentModalOpen(true)}
                onAddContractPayment={() => setIsAddingContractPayment(true)}
                onNewContractPaymentTypeChange={setNewContractPaymentType}
                onNewContractPaymentBankChange={setNewContractPaymentBank}
                onNewContractPaymentContractNumberChange={setNewContractPaymentContractNumber}
                onNewContractPaymentAmountChange={setNewContractPaymentAmount}
                onCreateContractPayment={handleCreateContractPayment}
                onDeleteContractPayment={handleDeleteContractPayment}
                onResetNewContractPaymentForm={resetNewContractPaymentForm}
                isDeletingPayment={isDeletingPayment}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};
